import { storeToRefs } from "pinia";
import { useLiveCallStore } from "./contact/live-call"
import { useAuthStore } from "./auth";
import { useCustomerEntryStore } from "./contact/customer-entry";
import { useDebounceFn } from "@vueuse/core";
import { ParticipantRole, Step, Suggestion } from "@/services/api/sdk";
import { useFields } from "@/composables/fields";
import { useCallSuggestionsStore } from "./contact/call-suggestions";
import { Ref, watch } from "vue";
import { useSentenceSimilarity } from "@/composables/sentence-similarity";
import { Result, useHelpCenterStore } from "./help-center";

export function setupDemo() {
    const { fields } = useFields();
    const liveCallStore = useLiveCallStore();
    const callSuggestionStore = useCallSuggestionsStore();
    const { suggestionsByFieldId, suggestions } = storeToRefs(callSuggestionStore);
    const customerEntryStore = useCustomerEntryStore();
    const { activeCall } = storeToRefs(liveCallStore);
    const { isDemoModeEnabled } = storeToRefs(useAuthStore());
    const { entriesByFieldId } = storeToRefs(customerEntryStore);

    async function commitSuggestion(suggestion: Suggestion) {
        await customerEntryStore.createEntry({
            customerId: activeCall.value!.customerId!,
            createContactEntryRequest: {
                contactId: liveCallStore.contactId || undefined,
                fieldId: suggestion.fieldId!,
                suggestionId: suggestion.id!,
            }
        });
    }

    const commitSuggestions = useDebounceFn(async () => {
        const suggestionsMap = new Map<string, Suggestion[]>();
        Object.entries(suggestionsByFieldId.value).forEach(([fieldIdString, suggestions]) => {
            const fieldId = parseInt(fieldIdString, 10);
            const existingEntries = entriesByFieldId.value[fieldIdString];
            if (existingEntries && existingEntries.length) {
                return;
            }
            const field = fields.value.find((f) => f.id === fieldId);
            if (!field) {
                return;
            }
            suggestionsMap.set(field.slug!, suggestions);
        });

        const firstNameSuggestions = suggestionsMap.get('first_name');
        const lastNameSuggestions = suggestionsMap.get('last_name');

        if (firstNameSuggestions && firstNameSuggestions.length && lastNameSuggestions && lastNameSuggestions.length) {
            const firstNameSuggestion = firstNameSuggestions[0];
            const lastNameSuggestion = lastNameSuggestions.filter(x => x.value?.valueString !== firstNameSuggestion.value?.valueString)[0];

            await commitSuggestion(firstNameSuggestion);
            await commitSuggestion(lastNameSuggestion);
        }
    }, 400);
    const unsubscribe = callSuggestionStore.$onAction(async ({ name, args, after }) => {
        if (!isDemoModeEnabled.value) {
            return;
        }
        if (name == 'addSuggestion') {
            if (args[0].value?.valueString === 'Andrew' || args[0].value?.valueString === 'Wilson') {
                after(() => {
                    const andrewIndex = suggestions.value.findIndex(x => x.value?.valueString == 'Andrew');
                    const wilsonIndex = suggestions.value.findIndex(x => x.value?.valueString == 'Wilson');
                    if (andrewIndex !== -1) {
                        suggestions.value.splice(andrewIndex, 1);
                    }
                    if (wilsonIndex !== -1) {
                        suggestions.value.splice(wilsonIndex, 1);
                    }
                });
                return;
            }
            const field = fields.value.find(x => x.id == args[0].fieldId);
            if (!field) {
                return;
            }
            if (field.slug == 'first_name' || field.slug == 'last_name') {
                await commitSuggestions();
            } else {
                await commitSuggestion(args[0]);
            }
        }
    });

    useHelpCenterDemo();

    return { unsubscribe };
}

const triggerSentences: Record<string, string> = {
    'Introduce yourself': 'Are you looking to save money or improve your Medicare benefits today?',
    'Consent to Call Recording': 'Do I have your permission to continue?',
    'Medicare parts A & B?': 'And do you have Medicare parts a and b?',
    'Federal Contracting Statement': 'Do I have your permission to continue?',
    'Confirm phone number': 'And if we get disconnected, is it okay if I call you back on that number?',
    'TPMO': 'Any information we provide is limited to those plans, additional options may be available, and you can contact medicare.gov or 18 hundred medicare medicare for a comprehensive list'
};

export function useHelpCenterDemo() {
    const liveCallStore = useLiveCallStore();

    const helpCenter = useHelpCenterStore();

    liveCallStore.$onAction(async ({ name, args }) => {
        if (name != 'updateUtterance' || args[0].participantRole != ParticipantRole.Customer) {
            return;
        }

        const utterance = args[0];

        const key = helpKeys[utterance.transcript!];
        if (!key) {
            return;
        }
        helpCenter.queueCandidate(utterance.transcript!, key);
    });
}

export function useScriptGuidanceDemo(currentStep: Ref<Step | undefined>, nextStepFn: () => void) {
    const customerEntryStore = useCustomerEntryStore();
    const liveCallStore = useLiveCallStore();

    const { entriesByFieldId } = storeToRefs(customerEntryStore);
    const { isDemoModeEnabled } = storeToRefs(useAuthStore());

    const { compareSentences } = useSentenceSimilarity();

    const { activePropertySearch, searches } = storeToRefs(useCallSuggestionsStore());

    let inFlightCounter = 0;

    const inFlights = new Map<number, number>();
    const doneIds = new Set<number>();

    watch(currentStep, () => {
        if (!currentStep.value?.fields?.length) {
            return;
        }
        const shouldOpenDrawer = currentStep.value?.fields.length === 1 && currentStep.value?.fields[0].type === 'Search' && currentStep.value?.fields[0].searchResultDisplayType !== 'Inline';

        if (shouldOpenDrawer) {
            activePropertySearch.value = currentStep.value?.fields[0];
        }
    });

    if (isDemoModeEnabled.value) {
        customerEntryStore.$onAction(({ name, after }) => {
            if (name !== 'createEntry') {
                return;
            }
            if (!currentStep.value?.fields?.length) {
                return;
            }
            after(() => {
                const hasMultiple = currentStep.value!.fields!.some(x => x.slug == 'doctors' || x.slug == 'pharmacies' || x.slug == 'rx');
                if (hasMultiple) {
                    return;
                }
                const entries = currentStep.value!.fields!.map(field => entriesByFieldId.value[field.id!.toString()] || []).flat();
                if (entries.length == currentStep.value?.fields?.length) {
                    nextStepFn();
                }
            });
        });

        liveCallStore.$onAction(async ({ name, args }) => {
            if (name != 'updateUtterance' || args[0].participantRole != 'Agent') {
                return;
            }

            const step = currentStep.value;
            if (!step) {
                return;
            }

            const stepId = step.id!;

            const triggerSentence = triggerSentences[step.heading!];
            if (!triggerSentence) {
                return;
            }

            var sentences = splitUtteranceIntoSentences(args[0].transcript!);

            var validSentences = sentences.filter(x => x.TokenCount > 4);

            if (!validSentences.length) {
                return;
            }

            const queryId = inFlightCounter++;

            inFlights.set(stepId, queryId);

            const split = validSentences.map(x => x.Text);

            var scores = await compareSentences([triggerSentence], split);

            if (doneIds.has(stepId)) {
                return;
            }

            // Step could have changed with concurrent sentence check already. Stop if that's the case
            if (inFlights.get(stepId) !== queryId) {
                return;
            }

            if (scores[0].some(x => x > 0.75)) {
                doneIds.add(stepId);
                nextStepFn();
            }
        });

        function useFakeSearchSuggestion(fieldSlug: string, needle: string) {
            liveCallStore.$onAction(async ({ name, args }) => {
                if (name != 'updateUtterance' || args[0].participantRole != 'Customer') {
                    return;
                }

                if (!currentStep.value?.fields || !currentStep.value.fields[0] || currentStep.value.fields[0].slug !== fieldSlug) {
                    return;
                }
                const field = currentStep.value.fields[0];
                if (!args[0].transcript?.includes(needle)) {
                    return;
                }

                const suggestions = searches.value[field.id!.toString()] || [];
                if (suggestions.every(x => x.query !== needle)) {
                    await liveCallStore.createSuggestionSearch({
                        fieldId: field.id!,
                        query: needle,
                    });
                }
            });
        }

        useFakeSearchSuggestion('pharmacies', 'Affinity Cares');
        useFakeSearchSuggestion('doctors', 'Robertson');
    }
}

const helpKeys: Record<string, string> = {
    'Hello, I got a new plan from you guys and they said I could get a food card for groceries. I got the card but it didn\'t work.': 'card_not_working',
};


const queries: Record<string, Result> = {
    'card_not_working': {
        text: `- The customer should have received a Humana Spending Account Card in the mail, which needs to be activated before use. They can activate it online at HealthyBenefitsPlus.com/Humana or by calling 855-396-0691 (TTY: 711), available 24/7.
- The customer's plan includes a $50 monthly Healthy Options allowance, which can be used for essential living expenses such as groceries, over-the-counter products, utilities, rent, pet food, and more.
- The unused portion of the monthly allowance carries over to the next month, but any unused amounts will expire at the end of the year.`,
        sources: [{
            title: 'Humana Spending account card',
            documentName: 'humana.com',
            excerpt: 'Once you receive your Humana Spending Account Card in the mail, you must activate it before you can use it',
            icon: 'link',
            url: 'https://www.humana.com/medicare/medicare-programs/spending-account-card',
        }, {
            title: 'Summary of Benefits',
            documentName: 'H5216-415.pdf',
            excerpt: '$50/mo. Healthy Options allowance with monthly fund carryover',
            icon: 'file',
            url: 'https://www.humana-medicare.com/BenefitSummary/2025PDFs/H5216415000SB25.pdf',
        }],
        spans: [{ start: 0, end: 236, source: 0 }, { start: 237, end: 439, source: 1 }, { start: 439, end: 576, source: 1 }]

    }
};

export function useDummyHelpCenter() {
    async function getResults(key: string): Promise<Result> {
        return queries[key];
    }

    return { getResults };
}

interface Sentence {
    StartOffset: number;
    EndOffset: number;
    TokenCount: number;
    Text: string;
}

export function splitUtteranceIntoSentences(transcript: string): Sentence[] {
    const reg = new RegExp("(?<=[.!?])\\s+", "g");

    const matches = Array.from(transcript.matchAll(reg));

    let lastOffset = 0;

    const sentences: Sentence[] = [];

    matches.forEach(match => {
        const startOffset = lastOffset;
        // If the sentence ends with a period, we remove it, to better match provided sentences
        const endOffset = match.index! - (transcript[match.index! - 1] === '.' ? 1 : 0);
        const text = transcript.substring(startOffset, endOffset);
        sentences.push({
            StartOffset: startOffset,
            EndOffset: endOffset,
            TokenCount: text.split(' ').length,
            Text: text,
        });
        lastOffset = match.index! + 1;
    });

    if (lastOffset < transcript.length) {
        const endOffset = transcript.length + (transcript[transcript.length - 1] === '.' ? 1 : 0);
        const text = transcript.substring(lastOffset, endOffset);
        sentences.push({
            StartOffset: lastOffset,
            EndOffset: transcript.length - 1,
            TokenCount: text.split(' ').length,
            Text: text,
        });
    }

    return sentences;
}